import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api";
import ErrorState from "./ErrorState";
import { IconCheck, IconClose, IconPlus, IconShield, IconTrash } from "../icons";
import { ordenarPorNome } from "../ordem";

/**
 * ACESSO E PERMISSÕES — quem entra em quê, e quem pode as duas etapas críticas.
 *
 * A tela responde três perguntas sobre cada pessoa, nesta ordem:
 *
 *   1. QUE ABAS ela vê        (Análises, Limites, Equipamentos...)
 *   2. O QUE ela pode FAZER   (diagnosticar, liberar ao cliente)
 *   3. ela ainda trabalha aqui? (senão, sai da lista)
 *
 * As duas espécies de permissão têm regras OPOSTAS, e isso é de propósito:
 *
 *   - AÇÃO: ninguém tem por padrão. Diagnosticar e liberar são as duas
 *     decisões que o laboratório assina — cadastrar alguém não dá esse poder.
 *
 *   - ABA: quem não tem NENHUMA marcada vê TODAS. Restringir é um ato
 *     deliberado ("esta pessoa só mexe em Análises"), não o estado inicial de
 *     todo mundo — senão uma atualização trancaria o sistema inteiro de uma vez.
 *
 * A regra vale no SERVIDOR (server/permissoes.ts): esconder o item do menu não
 * fecha nada, porque a URL continua respondendo. Aqui é conforto, lá é regra.
 */

type Dados = Awaited<ReturnType<typeof api.permissoes.listar>>;

/** Uma pessoa na lista, já com tudo o que a tela precisa mostrar dela. */
interface Pessoa {
  usuario: string;
  nome: string | null;
  ativo: boolean;
  /** Tem cadastro local no Laboratório (e não só permissão gravada)? */
  local: boolean;
}

export default function PermissoesView() {
  const [dados, setDados] = useState<Dados | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  /** Login digitado à mão — para quem ainda não entrou no Laboratório. */
  const [novoLogin, setNovoLogin] = useState("");
  /** Escapou da lista do Portal para digitar o login à mão (ver o bloco final). */
  const [digitarLogin, setDigitarLogin] = useState(false);
  /** De quem é o painel aberto (null = nenhum). */
  const [aberto, setAberto] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setDados(await api.permissoes.listar());
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * As linhas da tabela: os usuários do Laboratório MAIS quem tem permissão sem
   * ter cadastro local (a permissão pode ser concedida antes do primeiro acesso).
   */
  const pessoas = useMemo<Pessoa[]>(() => {
    if (!dados) return [];
    const mapa = new Map<string, Pessoa>();
    for (const u of dados.usuarios) {
      mapa.set(u.usuario.toLowerCase(), {
        usuario: u.usuario,
        nome: u.nome,
        ativo: u.ativo,
        local: true,
      });
    }
    for (const p of dados.permissoes) {
      const k = p.usuario.toLowerCase();
      if (!mapa.has(k)) {
        mapa.set(k, { usuario: p.usuario, nome: null, ativo: true, local: false });
      }
    }
    // A→Z pelo nome (ou pelo login, se não tiver nome) — ver src/ordem.ts.
    return ordenarPorNome([...mapa.values()], (p) => p.nome ?? p.usuario);
  }, [dados]);

  /**
   * QUEM O PORTAL TEM COM O LABORATÓRIO LIBERADO E AINDA NÃO ESTÁ NA TABELA.
   *
   * É a lista que o campo de conceder oferece. Sai dela quem já aparece acima —
   * o rótulo do campo é "que ainda não aparece na lista", e repetir a pessoa nos
   * dois lugares só faria alguém conceder duas vezes achando que não pegou.
   */
  const candidatos = useMemo(() => {
    const naTela = new Set(pessoas.map((p) => p.usuario.trim().toLowerCase()));
    // A→Z na tela (ver src/ordem.ts), mesmo que o servidor mude a ordem.
    return ordenarPorNome(
      (dados?.candidatos?.pessoas ?? []).filter((c) => !naTela.has(c.usuario.trim().toLowerCase())),
      (c) => c.nome ?? c.usuario,
    );
  }, [dados, pessoas]);

  /** O QUE O PORTAL DIZ SOBRE ESTE LOGIN (existe? tem o Laboratório liberado?). */
  const situacao = useCallback(
    (usuario: string) =>
      dados?.situacoes?.find((s) => s.usuario.toLowerCase() === usuario.toLowerCase()) ?? null,
    [dados],
  );

  const tem = useCallback(
    (usuario: string, permissao: string) =>
      !!dados?.permissoes.some(
        (p) => p.usuario.toLowerCase() === usuario.toLowerCase() && p.permissao === permissao,
      ),
    [dados],
  );

  /** As abas marcadas para a pessoa (vazio = ela vê todas). */
  const abasDe = useCallback(
    (usuario: string) =>
      (dados?.permissoes ?? [])
        .filter((p) => p.usuario.toLowerCase() === usuario.toLowerCase() && p.permissao.startsWith("aba:"))
        .map((p) => p.permissao.slice(4)),
    [dados],
  );

  const quemConcedeu = useCallback(
    (usuario: string, permissao: string) =>
      dados?.permissoes.find(
        (p) => p.usuario.toLowerCase() === usuario.toLowerCase() && p.permissao === permissao,
      ),
    [dados],
  );

  async function alternar(usuario: string, permissao: string, confirmarRetirada = true) {
    const chave = `${usuario}:${permissao}`;
    setSalvando(chave);
    try {
      if (tem(usuario, permissao)) {
        if (confirmarRetirada) {
          const nome =
            permissao === "diagnostico"
              ? "diagnóstico"
              : permissao === "liberacao_cliente"
                ? "liberação ao cliente"
                : (dados?.catalogo.find((c) => c.chave === permissao)?.rotulo.toLowerCase() ?? permissao);
          if (!confirm(`Tirar de ${usuario} a permissão de ${nome}?`)) return;
        }
        await api.permissoes.revogar(usuario, permissao);
      } else {
        await api.permissoes.conceder(usuario, permissao);
      }
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Não foi possível alterar a permissão");
    } finally {
      setSalvando(null);
    }
  }

  /**
   * Liga ou desliga a restrição de abas de uma pessoa.
   *
   * "Restringir" marca TODAS as abas de uma vez, e a pessoa vai desmarcando o
   * que não é dela. É o caminho mais curto: quase sempre se tira duas ou três
   * abas, não se escolhe dez.
   */
  async function alternarRestricao(usuario: string) {
    const marcadas = abasDe(usuario);
    const abas = dados?.catalogoAbas ?? [];
    setSalvando(`${usuario}:restricao`);
    try {
      if (marcadas.length > 0) {
        if (!confirm(`Tirar a restrição de abas de ${usuario}? Ela volta a ver o sistema inteiro.`))
          return;
        for (const key of marcadas) await api.permissoes.revogar(usuario, `aba:${key}`);
      } else {
        for (const a of abas) await api.permissoes.conceder(usuario, `aba:${a.key}`);
      }
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Não foi possível alterar as abas");
    } finally {
      setSalvando(null);
    }
  }

  /** Tira a pessoa do Laboratório (permissões + cadastro local). */
  async function excluir(pessoa: Pessoa) {
    const s = situacao(pessoa.usuario);
    const aviso =
      s?.existe === true && !s?.local
        ? `\n\nATENÇÃO: a conta ${pessoa.usuario} existe no PORTAL e continuará existindo. ` +
          `Para impedir que ela entre de novo, tire o Laboratório da lista de sistemas dela lá.`
        : "";
    if (
      !confirm(
        `Excluir ${pessoa.nome ?? pessoa.usuario} do Laboratório?\n\n` +
          `Isso apaga as permissões` +
          (pessoa.local ? " e o cadastro local dela neste sistema" : "") +
          `.${aviso}`,
      )
    )
      return;

    setSalvando(`${pessoa.usuario}:excluir`);
    try {
      const r = await api.permissoes.excluirUsuario(pessoa.usuario);
      if (aberto?.toLowerCase() === pessoa.usuario.toLowerCase()) setAberto(null);
      await load();
      alert(
        `${pessoa.usuario} saiu do Laboratório — ${r.permissoes_apagadas} permissão(ões) apagada(s)` +
          (r.cadastro_local_apagado ? " e o cadastro local removido." : "."),
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : "Não foi possível excluir");
    } finally {
      setSalvando(null);
    }
  }

  /** Concede a um login digitado (quem ainda não tem cadastro local). */
  async function adicionarLogin() {
    const login = novoLogin.trim();
    if (!login) return;
    setSalvando(login);
    try {
      await api.permissoes.conceder(login, "diagnostico");
      setNovoLogin("");
      await load();
      setAberto(login);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Não foi possível conceder a permissão");
    } finally {
      setSalvando(null);
    }
  }

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={load} />;
  if (!dados) return <div className="empty">Carregando...</div>;

  return (
    <div className="crud">
      <div className="modal-notice perm-aviso">
        <span className="modal-notice-ic">
          <IconShield size={17} />
        </span>
        <span>
          <strong>Abas</strong>: quem não tem nenhuma marcada vê o sistema inteiro — restringir é
          uma escolha, não o padrão. <strong>Diagnóstico</strong> e <strong>liberação</strong> são o
          contrário: ninguém tem, nem administrador, até ser marcado aqui.
          <br />
          Para a pessoa realmente trabalhar são três coisas: ter conta no <strong>Portal</strong>,
          ter o <strong>Laboratório liberado</strong> lá, e o que estiver marcado aqui{" "}
          <strong>no mesmo login</strong>. O que faltar aparece na linha.
        </span>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Pessoa</th>
              <th>Login</th>
              <th>Abas que vê</th>
              {dados.catalogo.map((c) => (
                <th key={c.chave} title={c.descricao}>
                  {c.rotulo}
                </th>
              ))}
              <th className="col-actions">Ações</th>
            </tr>
          </thead>
          <tbody>
            {pessoas.length === 0 ? (
              <tr>
                <td colSpan={4 + dados.catalogo.length} className="empty">
                  Ninguém cadastrado no Laboratório ainda.
                </td>
              </tr>
            ) : (
              pessoas.map((u) => {
                const s = situacao(u.usuario);
                const marcadas = abasDe(u.usuario);
                const restrita = marcadas.length > 0;
                const painelAberto = aberto?.toLowerCase() === u.usuario.toLowerCase();
                return (
                  /* A key vai no Fragment: são DUAS linhas por pessoa (a dela e
                     o painel de abas), e sem isto o React remonta as duas a
                     cada clique. */
                  <Fragment key={u.usuario}>
                    <tr className={painelAberto ? "perm-linha-aberta" : ""}>
                      <td>
                        <strong>{u.nome ?? s?.nome ?? "—"}</strong>
                        {!u.ativo && <div className="an-nota">usuário inativo</div>}
                      </td>
                      <td>
                        {u.usuario}
                        {s?.existe === false && (
                          <div className="an-nota">
                            <span
                              className="pill pill-danger"
                              title="Não há usuário com este login no Portal — o que estiver marcado ao lado não vale para ninguém. Confira o login exato ou crie a conta no Portal."
                            >
                              login não existe no Portal
                            </span>
                          </div>
                        )}
                        {s?.existe !== false && s?.acesso === false && (
                          <div className="an-nota">
                            <span
                              className="pill pill-warn"
                              title="A conta existe, mas o Laboratório não está na lista de sistemas dela — o portão barra na entrada, antes de qualquer permissão. Libere o sistema no Portal."
                            >
                              sem o Laboratório liberado no Portal
                            </span>
                          </div>
                        )}
                      </td>
                      <td>
                        <button
                          className={`perm-abas-resumo ${restrita ? "restrita" : ""}`}
                          onClick={() => setAberto(painelAberto ? null : u.usuario)}
                          title={
                            restrita
                              ? `Vê ${marcadas.length} de ${dados.catalogoAbas.length} abas — clique para escolher`
                              : "Vê todas as abas — clique para restringir"
                          }
                        >
                          {restrita ? `${marcadas.length} de ${dados.catalogoAbas.length}` : "Todas"}
                          <em>{painelAberto ? "fechar" : "escolher"}</em>
                        </button>
                      </td>
                      {dados.catalogo.map((c) => {
                        const marcado = tem(u.usuario, c.chave);
                        const origem = quemConcedeu(u.usuario, c.chave);
                        return (
                          <td key={c.chave}>
                            <button
                              className={`perm-toggle ${marcado ? "on" : "off"}`}
                              disabled={salvando === `${u.usuario}:${c.chave}`}
                              title={
                                marcado
                                  ? `Concedida por ${origem?.concedido_por ?? "—"} em ${
                                      origem
                                        ? new Date(origem.concedido_em).toLocaleDateString("pt-BR")
                                        : "—"
                                    } — clique para tirar`
                                  : "Clique para conceder"
                              }
                              onClick={() => void alternar(u.usuario, c.chave)}
                            >
                              {marcado ? <IconCheck size={15} /> : <IconClose size={15} />}
                              {marcado ? "Tem acesso" : "Sem acesso"}
                            </button>
                          </td>
                        );
                      })}
                      <td className="col-actions">
                        <button
                          className="icon-btn icon-btn-danger"
                          title={`Excluir ${u.usuario} do Laboratório`}
                          disabled={salvando === `${u.usuario}:excluir`}
                          onClick={() => void excluir(u)}
                        >
                          <IconTrash size={16} />
                        </button>
                      </td>
                    </tr>

                    {painelAberto && (
                      <tr className="perm-painel-linha">
                        <td colSpan={4 + dados.catalogo.length}>
                          <div className="perm-painel">
                            <div className="perm-painel-topo">
                              <div>
                                <strong>Abas de {u.nome ?? u.usuario}</strong>
                                <span className="muted">
                                  {restrita
                                    ? "Só as marcadas aparecem no menu dela. O servidor recusa as outras."
                                    : "Sem nenhuma marcada, ela vê o sistema inteiro."}
                                </span>
                              </div>
                              <button
                                className={restrita ? "btn-secondary" : "btn-primary"}
                                disabled={salvando === `${u.usuario}:restricao`}
                                onClick={() => void alternarRestricao(u.usuario)}
                              >
                                {salvando === `${u.usuario}:restricao`
                                  ? "Salvando..."
                                  : restrita
                                    ? "Liberar o sistema inteiro"
                                    : "Restringir abas"}
                              </button>
                            </div>

                            {restrita && (
                              <div className="perm-abas-grade">
                                {dados.catalogoAbas.map((a) => {
                                  const marcada = marcadas.includes(a.key);
                                  const chave = `${u.usuario}:aba:${a.key}`;
                                  return (
                                    <button
                                      key={a.key}
                                      className={`perm-aba ${marcada ? "on" : ""}`}
                                      disabled={salvando === chave}
                                      aria-pressed={marcada}
                                      onClick={() => void alternar(u.usuario, `aba:${a.key}`, false)}
                                    >
                                      {marcada ? <IconCheck size={14} /> : <IconClose size={14} />}
                                      {a.rotulo}
                                    </button>
                                  );
                                })}
                              </div>
                            )}

                            <p className="perm-painel-nota">
                              A aba <strong>Acesso e permissões</strong> não entra na marcação: ela é
                              de administrador. Se pudesse ser tirada, quem se restringisse sem
                              querer não teria por onde desfazer.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/*
        CONCEDER A QUEM AINDA NÃO ENTROU no Laboratório.

        A lista vem do Portal: são as pessoas com o Laboratório marcado no
        cadastro de usuário de lá. Escolher da lista em vez de digitar não é
        conforto — é o que impede o erro silencioso de conceder para "tainara"
        quem entra como "tainara.silva": a permissão vale para o texto gravado,
        e a tela mostrava as duas situações exatamente iguais.

        O campo de digitar continua disponível (e é o único caminho quando o
        Portal não responde), porque conceder antes de a conta existir é
        proposital — foi assim que a Tainara foi liberada.
      */}
      <div className="perm-novo">
        <label className="field">
          <span>Conceder diagnóstico a quem ainda não aparece na lista</span>
          {candidatos.length > 0 && !digitarLogin ? (
            <select value={novoLogin} onChange={(e) => setNovoLogin(e.target.value)}>
              <option value="">Escolha quem tem o Laboratório liberado no Portal...</option>
              {candidatos.map((c) => (
                <option key={c.usuario} value={c.usuario}>
                  {c.nome ?? c.usuario}
                  {c.cargo ? ` — ${c.cargo}` : ""} ({c.usuario})
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              placeholder="ex.: tainara"
              value={novoLogin}
              onChange={(e) => setNovoLogin(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void adicionarLogin();
              }}
            />
          )}
        </label>
        <button
          className="btn-secondary"
          onClick={() => void adicionarLogin()}
          disabled={!novoLogin.trim() || salvando === novoLogin.trim()}
        >
          <IconPlus size={16} /> Conceder
        </button>
      </div>

      {/* O motivo de a lista estar vazia, e a saída para os dois casos. */}
      <p className="perm-novo-ajuda">
        {candidatos.length > 0 && !digitarLogin ? (
          <>
            {candidatos.length} pessoa(s) com o Laboratório liberado no Portal e ainda sem acesso às
            etapas daqui.{" "}
            <button
              type="button"
              className="perm-link"
              onClick={() => {
                setDigitarLogin(true);
                setNovoLogin("");
              }}
            >
              Não está na lista? Digitar o login
            </button>
          </>
        ) : (
          <>
            {dados.candidatos?.mensagem ??
              "Digite o login exatamente como a pessoa entra no Portal — a permissão é gravada para esse texto."}{" "}
            {candidatos.length > 0 && (
              <button
                type="button"
                className="perm-link"
                onClick={() => {
                  setDigitarLogin(false);
                  setNovoLogin("");
                }}
              >
                Voltar para a lista
              </button>
            )}
          </>
        )}
      </p>
    </div>
  );
}
